import { supabase } from '@/integrations/supabase/client';

export type FeedbackType = 'positive' | 'negative';

export interface MessageFeedback {
  id: string;
  message_id: string;
  user_id: string;
  feedback_type: FeedbackType;
  comment?: string;
  created_at: string;
}

export async function submitFeedback(
  messageId: string,
  userId: string,
  feedbackType: FeedbackType
): Promise<MessageFeedback | null> {
  // Use upsert to handle both insert and update
  const { data, error } = await supabase
    .from('message_feedback')
    .upsert(
      {
        message_id: messageId,
        user_id: userId,
        feedback_type: feedbackType,
      },
      {
        onConflict: 'message_id,user_id',
      }
    )
    .select()
    .single();

  if (error) {
    console.error('Error submitting feedback:', error);
    throw error;
  }

  return data as MessageFeedback;
}

export async function getFeedback(
  messageId: string,
  userId: string
): Promise<MessageFeedback | null> {
  const { data, error } = await supabase
    .from('message_feedback')
    .select('*')
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error getting feedback:', error);
    return null;
  }

  return data as MessageFeedback | null;
}

export async function deleteFeedback(
  messageId: string,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from('message_feedback')
    .delete()
    .eq('message_id', messageId)
    .eq('user_id', userId);

  if (error) {
    console.error('Error deleting feedback:', error);
    throw error;
  }
}

// Admin functions for dashboard analytics
export async function getFeedbackStats(): Promise<{
  totalPositive: number;
  totalNegative: number;
  satisfactionRate: number;
  recentFeedback: MessageFeedback[];
}> {
  const { count: positiveCount } = await supabase
    .from('message_feedback')
    .select('*', { count: 'exact', head: true })
    .eq('feedback_type', 'positive');

  const { count: negativeCount } = await supabase
    .from('message_feedback')
    .select('*', { count: 'exact', head: true })
    .eq('feedback_type', 'negative');

  const { data: recentFeedback } = await supabase
    .from('message_feedback')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(10);

  const total = (positiveCount || 0) + (negativeCount || 0);
  const satisfactionRate = total > 0 
    ? Math.round(((positiveCount || 0) / total) * 100) 
    : 0;

  return {
    totalPositive: positiveCount || 0,
    totalNegative: negativeCount || 0,
    satisfactionRate,
    recentFeedback: (recentFeedback as MessageFeedback[]) || [],
  };
}

export async function getDailyFeedbackStats(days: number = 30): Promise<{
  date: string;
  positive: number;
  negative: number;
}[]> {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const { data } = await supabase
    .from('message_feedback')
    .select('feedback_type, created_at')
    .gte('created_at', startDate.toISOString())
    .order('created_at', { ascending: true });

  // Group by day
  const dailyData: Record<string, { positive: number; negative: number }> = {};
  
  for (let i = 0; i < days; i++) {
    const date = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
    const dateKey = date.toISOString().split('T')[0];
    dailyData[dateKey] = { positive: 0, negative: 0 };
  }

  data?.forEach((feedback) => {
    const dateKey = feedback.created_at.split('T')[0];
    if (dailyData[dateKey]) {
      if (feedback.feedback_type === 'positive') {
        dailyData[dateKey].positive++;
      } else {
        dailyData[dateKey].negative++;
      }
    }
  });

  return Object.entries(dailyData).map(([date, counts]) => ({
    date,
    ...counts,
  }));
}
